import type { TFunction } from "i18next";
import type { WageSettingsResponse } from "../../../types/wageRules";
import type { BreakContribution, HoursRateContribution, OvertimeContribution } from "../../../utils/models";
import { matchHoursRateRows, matchOvertimeRows } from "../../team/WageSettings/deepLinkRowMatchers";
import { breakRulesCellText, hoursRateCellText, overtimeCellText, type RuleCellFormat } from "../../team/WageSettings/wageRuleCellText";
import type { ConfigRowsPreviewRow } from "../../UIToolkit/Tables/ConfigTable/ConfigRowsPreview";
import {
	breakRulesChanged,
	breakRulesMatches,
	capturedBreakRulesRow,
	capturedHoursRateRow,
	capturedOvertimeRow,
	hoursRateChanged,
	hoursRateMatches,
	overtimeChanged,
	overtimeMatches,
} from "./liveRuleRows";
import type { LegacyRuleTarget, RuleSubject } from "./ruleCardSubjects";

/*
 * DAT-906 · The rows each section of a hover card shows, from today's Wage Settings ("live") or — where
 * those cannot be read — from what the breakdown captured when the shift was priced.
 */

/** The Wage Settings table a section shows rows of. */
export type RuleSectionKind = "hours_rate" | "overtime" | "break";

/** Where the rows come from: today's stored rows of the rule's department, or the breakdown's own capture. */
export type RuleRowsSource = { type: "live"; settings: WageSettingsResponse } | { type: "captured" };

/**
 * `id` tells two sections of one table apart (a captured rule's and a legacy row's). `isLegacy`: a legacy
 * row's section, which says all there is to say about its rows itself. `mentionsJobs`: a row shown is
 * limited to specific jobs — what decides the Job column where the department's own job setup is not known.
 */
export type RuleSectionModel = {
	id: string;
	kind: RuleSectionKind;
	rows: ConfigRowsPreviewRow[];
	messages: string[];
	isLegacy: boolean;
	mentionsJobs: boolean;
};

/** Every stored rule row can be limited to jobs (`[]`/unset = all). */
type JobScopedRow = { jobIds?: readonly string[] };
const limitsJobs = (row: JobScopedRow): boolean => row.jobIds !== undefined && row.jobIds.length > 0;

/** A cell the capture does not record (a break rule's type and start): shown as a dash, never guessed. */
const NOT_RECORDED = "–";

/** A rule a breakdown line captured: its name, and who it applied to. */
type CapturedRule = { name: string; scope: { jobIds: readonly string[] } };

type Spec<C extends CapturedRule, TRow extends JobScopedRow> = {
	kind: RuleSectionKind;
	table: (settings: WageSettingsResponse) => readonly TRow[];
	matches: (rows: readonly TRow[], c: C) => number[];
	changed: (row: TRow, c: C) => boolean;
	liveCells: (f: RuleCellFormat, row: TRow) => Record<string, string>;
	capturedCells: (f: RuleCellFormat, c: C) => Record<string, string>;
};

const HOURS_RATE: Spec<HoursRateContribution, WageSettingsResponse["hoursRateConfig"][number]> = {
	kind: "hours_rate",
	table: (settings) => settings.hoursRateConfig,
	matches: hoursRateMatches,
	changed: hoursRateChanged,
	liveCells: hoursRateCellText,
	capturedCells: (f, c) => hoursRateCellText(f, capturedHoursRateRow(c)),
};

const OVERTIME: Spec<OvertimeContribution, WageSettingsResponse["overtimeConfig"][number]> = {
	kind: "overtime",
	table: (settings) => settings.overtimeConfig,
	matches: overtimeMatches,
	changed: overtimeChanged,
	liveCells: overtimeCellText,
	capturedCells: (f, c) => overtimeCellText(f, capturedOvertimeRow(c)),
};

const BREAK_RULES: Spec<BreakContribution, WageSettingsResponse["breakRulesConfig"][number]> = {
	kind: "break",
	table: (settings) => settings.breakRulesConfig,
	matches: breakRulesMatches,
	changed: breakRulesChanged,
	liveCells: breakRulesCellText,
	capturedCells: (f, c) => {
		const cells = breakRulesCellText(f, capturedBreakRulesRow(c));
		return {
			...cells,
			type: c.ruleType === null ? NOT_RECORDED : cells.type,
			breakStart: c.startsAfterHours === null ? NOT_RECORDED : cells.breakStart,
		};
	},
};

const ruleName = (t: TFunction, name: string): string => (name.trim() === "" ? t("wageBreakdown.rule.unnamed") : name);

/** Today's rows for each rule — each row once, however many lines name it — and a note for a rule that is gone. */
const liveSection = <C extends CapturedRule, TRow extends JobScopedRow>(
	spec: Spec<C, TRow>,
	contributions: readonly C[],
	settings: WageSettingsResponse,
	f: RuleCellFormat,
): RuleSectionModel => {
	const table = spec.table(settings);
	const shown = new Set<number>();
	const rows: ConfigRowsPreviewRow[] = [];
	const messages: string[] = [];
	for (const c of contributions) {
		const indices = spec.matches(table, c);
		if (indices.length === 0) messages.push(f.t("wageBreakdown.card.ruleRemoved", { name: ruleName(f.t, c.name) }));
		for (const index of indices.filter((i) => !shown.has(i))) {
			shown.add(index);
			const row = table[index];
			rows.push({ id: `${spec.kind}-${index}`, cells: spec.liveCells(f, row), ...(spec.changed(row, c) ? { note: f.t("wageBreakdown.card.changed") } : {}) });
		}
	}
	const mentionsJobs = [...shown].some((index) => limitsJobs(table[index]));
	return { id: spec.kind, kind: spec.kind, rows, messages, isLegacy: false, mentionsJobs };
};

const capturedSection = <C extends CapturedRule, TRow extends JobScopedRow>(
	spec: Spec<C, TRow>,
	contributions: readonly C[],
	f: RuleCellFormat,
): RuleSectionModel => ({
	id: spec.kind,
	kind: spec.kind,
	rows: contributions.map((c, index) => ({ id: `${spec.kind}-captured-${index}`, cells: spec.capturedCells(f, c) })),
	messages: [],
	isLegacy: false,
	mentionsJobs: contributions.some((c) => c.scope.jobIds.length > 0),
});

const section = <C extends CapturedRule, TRow extends JobScopedRow>(
	spec: Spec<C, TRow>,
	contributions: readonly C[],
	source: RuleRowsSource,
	f: RuleCellFormat,
): RuleSectionModel[] => {
	if (contributions.length === 0) return [];
	return [source.type === "live" ? liveSection(spec, contributions, source.settings, f) : capturedSection(spec, contributions, f)];
};

const LEGACY_SECTION_KIND: Readonly<Record<LegacyRuleTarget["section"], RuleSectionKind>> = {
	"hours-rate": "hours_rate",
	overtime: "overtime",
	"break-rules": "break",
};

const everyIndex = (rows: readonly unknown[]): number[] => rows.map((_, index) => index);

/**
 * Today's rows a legacy rate type points at, with their cells: an hours-rate rule by its name; overtime,
 * every rule of the kind the label names (every overtime rule for a bare "OT"); a break, every break rule —
 * the label names no rule, so the department's rules are what could have placed it.
 */
type LegacyRow = { index: number; cells: Record<string, string>; limitsJobs: boolean };

const legacyRow = <TRow extends JobScopedRow>(rows: readonly TRow[], index: number, cellsOf: (row: TRow) => Record<string, string>): LegacyRow => ({
	index,
	cells: cellsOf(rows[index]),
	limitsJobs: limitsJobs(rows[index]),
});

const legacyRows = (target: LegacyRuleTarget, settings: WageSettingsResponse, f: RuleCellFormat): LegacyRow[] => {
	switch (target.section) {
		case "hours-rate":
			return matchHoursRateRows(target, settings.hoursRateConfig).map((index) =>
				legacyRow(settings.hoursRateConfig, index, (row) => hoursRateCellText(f, row)),
			);
		case "overtime": {
			const rows = settings.overtimeConfig;
			const indices = target.overtimeKind === undefined ? everyIndex(rows) : matchOvertimeRows(target, rows);
			return indices.map((index) => legacyRow(rows, index, (row) => overtimeCellText(f, row)));
		}
		case "break-rules":
			return everyIndex(settings.breakRulesConfig).map((index) => legacyRow(settings.breakRulesConfig, index, (row) => breakRulesCellText(f, row)));
	}
};

/**
 * A legacy row's section. Nothing was captured, so there is no "as priced" to fall back to or compare with:
 * without today's rows — which for a legacy row means the read failed, since a viewer who may not make it
 * gets no legacy section (`DepartmentRuleSections`) — it says the rules couldn't be loaded.
 */
const legacySection = (subject: Extract<RuleSubject, { kind: "legacy" }>, source: RuleRowsSource, f: RuleCellFormat): RuleSectionModel => {
	const kind = LEGACY_SECTION_KIND[subject.target.section];
	const id = `${kind}-legacy`;
	if (source.type === "captured")
		return {
			id,
			kind,
			rows: [],
			messages: [f.t("wageBreakdown.card.legacyLoadError", { rateType: subject.rateType })],
			isLegacy: true,
			mentionsJobs: false,
		};
	const matched = legacyRows(subject.target, source.settings, f);
	const rows = matched.map(({ index, cells }) => ({ id: `${kind}-${index}`, cells }));
	const messages = [f.t(rows.length === 0 ? "wageBreakdown.card.legacyNoMatch" : "wageBreakdown.card.legacyMatch", { rateType: subject.rateType })];
	return { id, kind, rows, messages, isLegacy: true, mentionsJobs: matched.some((row) => row.limitsJobs) };
};

/** A card's rule sections, in the order Wage Settings shows them: hours rates, overtime, break rules. */
export const ruleSections = (subjects: readonly RuleSubject[], source: RuleRowsSource, f: RuleCellFormat): RuleSectionModel[] => [
	...section(
		HOURS_RATE,
		subjects.flatMap((s) => (s.kind === "hours_rate" ? [s.contribution] : [])),
		source,
		f,
	),
	...section(
		OVERTIME,
		subjects.flatMap((s) => (s.kind === "overtime" ? [s.contribution] : [])),
		source,
		f,
	),
	...section(
		BREAK_RULES,
		subjects.flatMap((s) => (s.kind === "break" ? [s.contribution] : [])),
		source,
		f,
	),
	...subjects.flatMap((s) => (s.kind === "legacy" ? [legacySection(s, source, f)] : [])),
];

/** The section each subject will show once its rows arrive — drawn as placeholders while they load. */
export const sectionKindOf = (subject: RuleSubject): RuleSectionKind =>
	subject.kind === "legacy" ? LEGACY_SECTION_KIND[subject.target.section] : subject.kind;
